export const CANOPY_MAX_NEIGHBORS = 10;

export type CanopyNodeRole = 'query' | 'neighbor' | 'ancestor';

export type CanopyNode = {
  id: string;
  label: string;
  role: CanopyNodeRole;
  score: number | null;
  inDisgenet?: boolean;
  /** Genes for the active source (resolved ALL vs CURATED). */
  totalGenes?: number | null;
  totalGenesAll?: number | null;
  totalGenesCurated?: number | null;
};

export type CanopyEdge = {
  child: string;
  parent: string;
};

export type CanopyResponse = {
  query: string;
  nodes: CanopyNode[];
  edges: CanopyEdge[];
};

export type CanopyLinkKind = 'parent' | 'child' | 'via-neighbor';

export type CanopyTreeDatum = {
  id: string;
  label: string;
  role: CanopyNodeRole;
  score: number | null;
  inDisgenet?: boolean;
  totalGenes?: number | null;
  totalGenesAll?: number | null;
  totalGenesCurated?: number | null;
  linkKind?: CanopyLinkKind;
  viaId?: string;
  children?: CanopyTreeDatum[];
};

export type CanopyTreeBuildResult = {
  tree: CanopyTreeDatum;
  neighborCount: number;
  edgeCount: number;
  edges: CanopyEdge[];
};

export const MOCK_CANOPY_RESPONSE: CanopyResponse = {
  query: 'C0002395',
  nodes: [
    { id: 'C0002395', label: "Alzheimer's disease", role: 'query', score: null, inDisgenet: true },
    { id: 'C0011268', label: 'Senile dementia', role: 'neighbor', score: 0.979377, inDisgenet: true },
    { id: 'C0011265', label: 'Presenile dementia', role: 'neighbor', score: 0.975159, inDisgenet: true },
    {
      id: 'C0338632',
      label: 'Dementia associated with another disease',
      role: 'neighbor',
      score: 0.949411,
      inDisgenet: false,
    },
    {
      id: 'C0154669',
      label: 'Senile degeneration of brain',
      role: 'neighbor',
      score: 0.933812,
      inDisgenet: true,
    },
    {
      id: 'C0241816',
      label: 'Global brain atrophy',
      role: 'neighbor',
      score: 0.921173,
      inDisgenet: true,
    },
    { id: 'C0497327', label: 'Dementia', role: 'neighbor', score: 0.911654, inDisgenet: true },
    {
      id: 'C0154668',
      label: 'Other cerebral degenerations',
      role: 'neighbor',
      score: 0.906522,
      inDisgenet: true,
    },
    {
      id: 'C0011269',
      label: 'Dementia, Vascular',
      role: 'neighbor',
      score: 0.89194,
      inDisgenet: true,
    },
    {
      id: 'C5779573',
      label: 'Primary degenerative dementia of the Alzheimer type',
      role: 'neighbor',
      score: 0.889091,
      inDisgenet: true,
    },
    {
      id: 'C0494463',
      label: 'Primary progressive dementia of the Alzheimer type',
      role: 'neighbor',
      score: 0.887394,
      inDisgenet: true,
    },
  ],
  edges: [
    { child: 'C0002395', parent: 'C0011268' },
    { child: 'C0002395', parent: 'C0154668' },
    { child: 'C0002395', parent: 'C0154669' },
    { child: 'C0002395', parent: 'C0241816' },
    { child: 'C0002395', parent: 'C0497327' },
    { child: 'C0011265', parent: 'C0002395' },
    { child: 'C0011269', parent: 'C0011265' },
    { child: 'C0338632', parent: 'C0011265' },
    { child: 'C0494463', parent: 'C0002395' },
    { child: 'C5779573', parent: 'C0002395' },
  ],
};

function scoreValue(score: number | null | undefined): number {
  if (typeof score !== 'number' || !Number.isFinite(score)) return Number.NEGATIVE_INFINITY;
  return score;
}

function normalizeRole(role: unknown, id: string, queryId: string): CanopyNodeRole {
  if (role === 'query' || id === queryId) return 'query';
  if (role === 'ancestor') return 'ancestor';
  return 'neighbor';
}

function parseOptionalCount(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) return value;
  if (value == null || value === '') return null;
  const asNumber = Number(value);
  return Number.isFinite(asNumber) && asNumber >= 0 ? asNumber : null;
}

function readNestedGeneCounts(value: unknown): {
  totalGenesAll: number | null;
  totalGenesCurated: number | null;
} {
  if (!value || typeof value !== 'object') {
    return { totalGenesAll: null, totalGenesCurated: null };
  }
  const data = value as Record<string, unknown>;
  return {
    totalGenesAll: parseOptionalCount(data.all ?? data.ALL ?? data.total_all),
    totalGenesCurated: parseOptionalCount(data.curated ?? data.CURATED ?? data.total_curated),
  };
}

function toTreeNode(node: CanopyNode): CanopyTreeDatum {
  return {
    id: node.id,
    label: node.label || node.id,
    role: node.role,
    score: node.score,
    inDisgenet: node.inDisgenet,
    totalGenes: node.totalGenes,
    totalGenesAll: node.totalGenesAll,
    totalGenesCurated: node.totalGenesCurated,
  };
}

export function resolveCanopyTotalGenes(
  node: Pick<CanopyNode, 'totalGenes' | 'totalGenesAll' | 'totalGenesCurated'>,
  source: 'ALL' | 'CURATED',
): number | null {
  if (source === 'CURATED') {
    const curated = parseOptionalCount(node.totalGenesCurated);
    if (curated != null) return curated;
  } else {
    const all = parseOptionalCount(node.totalGenesAll);
    if (all != null) return all;
  }
  return parseOptionalCount(node.totalGenes);
}

export function formatCanopyTotalGenes(totalGenes: number | null | undefined): string {
  if (typeof totalGenes !== 'number' || !Number.isFinite(totalGenes)) return '—';
  const count = Math.round(totalGenes);
  const label = count === 1 ? 'gene' : 'genes';
  return `${count.toLocaleString('en-US')} ${label}`;
}

export function buildCanopyGeneCountsEndpoint(
  diseaseIds: string[],
  source: 'ALL' | 'CURATED',
  isAuthenticated?: boolean,
): string {
  const uniqueIds = [...new Set(diseaseIds.map(id => id.trim()).filter(Boolean))];
  const params = new URLSearchParams({
    disease: uniqueIds.join(','),
    source,
    page_number: '0',
    page_size: String(Math.max(uniqueIds.length, 1)),
  });
  if (!isAuthenticated) params.set('skey', 'unauth');
  return `api/disease/all?${params.toString()}`;
}

export function parseCanopyGeneCountsPayload(payload: unknown): Map<string, number> {
  const rows = Array.isArray(payload)
    ? payload
    : payload && typeof payload === 'object' && Array.isArray((payload as { records?: unknown }).records)
      ? ((payload as { records: unknown[] }).records ?? [])
      : [];

  const counts = new Map<string, number>();
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const data = row as Record<string, unknown>;
    const id = String(data.diseaseUMLSCUI ?? data.disease_umls_cui ?? data.diseaseId ?? data.id ?? '').trim();
    const count = parseOptionalCount(
      data.numGenesAssociatedToDisease ?? data.num_genes ?? data.total_genes ?? data.geneCount,
    );
    if (id && count != null) counts.set(id, count);
  }
  return counts;
}

export function applyCanopyTotalGenes(
  response: CanopyResponse,
  source: 'ALL' | 'CURATED',
  counts?: Map<string, number>,
): CanopyResponse {
  return {
    ...response,
    nodes: response.nodes.map(node => {
      const fromMap = counts?.get(node.id);
      const resolved =
        fromMap != null
          ? fromMap
          : resolveCanopyTotalGenes(node, source);
      return {
        ...node,
        totalGenes: resolved,
      };
    }),
  };
}

function expandKeptIdsWithBridgeNodes(
  keptIds: Set<string>,
  edges: CanopyEdge[] | undefined,
  nodesById: Map<string, CanopyNode>,
): void {
  let expanded = true;
  while (expanded) {
    expanded = false;
    for (const edge of edges ?? []) {
      const parent = String(edge?.parent ?? '');
      const child = String(edge?.child ?? '');
      if (!parent || !child || parent === child) continue;
      if (!nodesById.has(parent) || !nodesById.has(child)) continue;

      if (keptIds.has(parent) && !keptIds.has(child)) {
        const missing = nodesById.get(child);
        if (missing?.role === 'ancestor') {
          keptIds.add(child);
          expanded = true;
        }
      } else if (keptIds.has(child) && !keptIds.has(parent)) {
        const missing = nodesById.get(parent);
        if (missing?.role === 'ancestor') {
          keptIds.add(parent);
          expanded = true;
        }
      }
    }
  }
}

export function buildCanopyTreeData(
  response: CanopyResponse | null | undefined,
  maxNeighbors: number = CANOPY_MAX_NEIGHBORS,
): CanopyTreeBuildResult | null {
  if (!response?.query || !Array.isArray(response.nodes) || response.nodes.length === 0) {
    return null;
  }

  const queryId = String(response.query);
  const nodesById = new Map<string, CanopyNode>();
  for (const node of response.nodes) {
    if (!node?.id) continue;
    const id = String(node.id);
    nodesById.set(id, {
      id,
      label: String(node.label ?? id),
      role: normalizeRole(node.role, id, queryId),
      score: typeof node.score === 'number' && Number.isFinite(node.score) ? node.score : null,
      inDisgenet: node.inDisgenet === true,
      totalGenes: parseOptionalCount(node.totalGenes),
      totalGenesAll: parseOptionalCount(node.totalGenesAll),
      totalGenesCurated: parseOptionalCount(node.totalGenesCurated),
    });
  }

  const queryNode = nodesById.get(queryId) ?? {
    id: queryId,
    label: queryId,
    role: 'query' as const,
    score: null,
    totalGenes: null,
  };

  const rankedNeighbors = [...nodesById.values()]
    .filter(node => node.id !== queryId && node.role !== 'ancestor')
    .sort((a, b) => scoreValue(b.score) - scoreValue(a.score))
    .slice(0, Math.max(0, maxNeighbors));

  const keptIds = new Set<string>([queryId, ...rankedNeighbors.map(node => node.id)]);
  expandKeptIdsWithBridgeNodes(keptIds, response.edges, nodesById);

  const keptEdges: CanopyEdge[] = [];
  for (const edge of response.edges ?? []) {
    const parent = String(edge?.parent ?? '');
    const child = String(edge?.child ?? '');
    if (!parent || !child || parent === child) continue;
    if (!keptIds.has(parent) || !keptIds.has(child)) continue;
    keptEdges.push({ parent, child });
  }

  const linkKindById = new Map<string, CanopyLinkKind>();
  const viaIdById = new Map<string, string>();

  for (const edge of keptEdges) {
    if (edge.parent === queryId) {
      linkKindById.set(edge.child, 'child');
    } else if (edge.child === queryId) {
      linkKindById.set(edge.parent, 'parent');
    } else if (!linkKindById.has(edge.child)) {
      linkKindById.set(edge.child, 'via-neighbor');
      viaIdById.set(edge.child, edge.parent);
    }
  }

  const displayNodes = [...keptIds]
    .filter(id => id !== queryId)
    .map(id => nodesById.get(id)!)
    .sort((a, b) => {
      if (a.role === 'ancestor' && b.role !== 'ancestor') return 1;
      if (b.role === 'ancestor' && a.role !== 'ancestor') return -1;
      return scoreValue(b.score) - scoreValue(a.score);
    });

  const displayById = new Map(displayNodes.map(node => [node.id, node]));
  const nestedChildrenByParent = new Map<string, CanopyNode[]>();
  for (const edge of keptEdges) {
    if (edge.parent === queryId || edge.child === queryId) continue;
    const child = displayById.get(edge.child);
    if (!child) continue;
    const children = nestedChildrenByParent.get(edge.parent) ?? [];
    children.push(child);
    nestedChildrenByParent.set(edge.parent, children);
  }

  const orderedNodes: CanopyNode[] = [];
  const addedIds = new Set<string>();
  for (const node of displayNodes) {
    if (addedIds.has(node.id)) continue;
    orderedNodes.push(node);
    addedIds.add(node.id);
    for (const child of nestedChildrenByParent.get(node.id) ?? []) {
      if (addedIds.has(child.id)) continue;
      orderedNodes.push(child);
      addedIds.add(child.id);
    }
  }

  const tree: CanopyTreeDatum = {
    ...toTreeNode(queryNode),
    children: orderedNodes.map(node => {
      const datum = toTreeNode(node);
      datum.linkKind = linkKindById.get(node.id) ?? (node.role === 'ancestor' ? 'via-neighbor' : 'child');
      datum.viaId = viaIdById.get(node.id);
      return datum;
    }),
  };

  return {
    tree,
    neighborCount: rankedNeighbors.length,
    edgeCount: keptEdges.length,
    edges: keptEdges,
  };
}

export function formatCanopyScore(score: number | null | undefined): string {
  if (typeof score !== 'number' || !Number.isFinite(score)) return '—';
  return score.toFixed(3);
}

export function resolveCanopyQueryId(row: Record<string, unknown> | null | undefined): string {
  if (!row) return '';
  const candidates = [
    row.diseaseUMLSCUI,
    row.disease_umls_cui,
    row.diseaseId,
    row.disease_id,
    row.conceptId,
    row.concept_id,
    row.id,
  ];
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return '';
}

export function parseCanopyGraphPayload(payload: unknown): CanopyResponse | null {
  if (!payload || typeof payload !== 'object') return null;
  const data = payload as Record<string, unknown>;
  const query = typeof data.query === 'string' ? data.query.trim() : '';
  if (!query || !Array.isArray(data.nodes)) return null;

  const nodes: CanopyNode[] = data.nodes
    .filter((node): node is Record<string, unknown> => !!node && typeof node === 'object')
    .map(node => {
      const id = String(node.id ?? '').trim();
      const scoreRaw = node.score;
      const score =
        typeof scoreRaw === 'number' && Number.isFinite(scoreRaw)
          ? scoreRaw
          : scoreRaw == null
            ? null
            : Number.isFinite(Number(scoreRaw))
              ? Number(scoreRaw)
              : null;
      const nested = readNestedGeneCounts(node.total_genes ?? node.totalGenes);
      const totalGenesAll = parseOptionalCount(
        node.total_genes_all ??
          node.totalGenesAll ??
          node.n_genes_all ??
          node.numGenesAssociatedToDisease_ALL ??
          nested.totalGenesAll,
      );
      const totalGenesCurated = parseOptionalCount(
        node.total_genes_curated ??
          node.totalGenesCurated ??
          node.n_genes_curated ??
          node.numGenesAssociatedToDisease_CURATED ??
          nested.totalGenesCurated,
      );
      const totalGenes = parseOptionalCount(
        node.total_genes ?? node.totalGenes ?? node.geneCount ?? node.n_genes ?? node.num_genes,
      );

      return {
        id,
        label: String(node.label ?? id),
        role: normalizeRole(node.role, id, query),
        score,
        inDisgenet: node.in_disgenet === true || node.inDisgenet === true,
        totalGenes,
        totalGenesAll,
        totalGenesCurated,
      };
    })
    .filter(node => node.id);

  if (nodes.length === 0) return null;

  const edges: CanopyEdge[] = Array.isArray(data.edges)
    ? data.edges
        .filter((edge): edge is Record<string, unknown> => !!edge && typeof edge === 'object')
        .map(edge => ({
          parent: String(edge.parent ?? '').trim(),
          child: String(edge.child ?? '').trim(),
        }))
        .filter(edge => edge.parent && edge.child)
    : [];

  return { query, nodes, edges };
}

export function buildCanopyGraphEndpoint(
  queryId: string,
  limit: number = CANOPY_MAX_NEIGHBORS,
  isAuthenticated?: boolean,
): string {
  const params = new URLSearchParams({
    query: queryId,
    limit: String(limit),
  });
  if (!isAuthenticated) params.set('skey', 'unauth');
  return `api/ontology/graph?${params.toString()}`;
}
